import React, { useEffect, useState } from "react";
import {
    Card, Typography, Table, Input, Select, Button, Space, Tag,
    Row, Col, Empty, Progress,
} from "antd";
import {
    PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined,
    DownOutlined, ArrowLeftOutlined, DownloadOutlined, FilterOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { ProjectTaskService } from "../../../Services/Manage/ProjectTaskService";
import { MilestoneService } from "../../../Services/Manage/MilestoneService";
import { UserService } from "../../../Services/Manage/UserService";

const { Text } = Typography;

const STATUS_COLORS = {
    "To Do": "#1677ff",
    "In Progress": "#fa8c16",
    Completed: "#52c41a",
    "On Hold": "#8c8c8c",
    Cancelled: "#f5222d",
};

const PRIORITY_COLORS = {
    High: "#f5222d",
    Medium: "#fa8c16",
    Low: "#52c41a",
};

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

const TasksList = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const perm = getPermission("/overview-project");
    const viewAll = perm.view_all === true;

    const [userMap, setUserMap] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const res = await UserService.getAllUsers();
                const map = {};
                (res.data || []).forEach((u) => {
                    if (u.user_id) map[String(u.user_id)] = u.name;
                });
                setUserMap(map);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    const project = {
        id: location.state?.id || null,
        pro_name: location.state?.pro_name || "-",
        pro_no: location.state?.pro_no || "-",
        customer: location.state?.pro_customer || "-",
        managerId: location.state?.pro_manager || null,
        value: location.state?.pro_value || 0,
        engineerIds: location.state?.pro_engineer || [],
        departmentName: location.state?.pro_department_name || "-",
        start_date: (location.state?.pro_start_date && dayjs(location.state.pro_start_date).isValid())
            ? dayjs(location.state.pro_start_date).format("DD-MMM-YYYY")
            : "-",
        end_date: (location.state?.pro_target_end_date && dayjs(location.state.pro_target_end_date).isValid())
            ? dayjs(location.state.pro_target_end_date).format("DD-MMM-YYYY")
            : "-",
        status: location.state?.pro_status || "Not Started",
    };

    const managerName = userMap[String(project.managerId)] || project.managerId || "-";

    const [tasks, setTasks] = useState([]);
    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [milestoneFilter, setMilestoneFilter] = useState(null);
const [isProjectManager, setIsProjectManager] = useState(false);
    const [permissionsReady, setPermissionsReady] = useState(false);
    const [collapsedMilestones, setCollapsedMilestones] = useState(new Set());
    const [defaultCollapseApplied, setDefaultCollapseApplied] = useState(false);

    const toggleMilestone = (code) => {
        setCollapsedMilestones((prev) => {
            const next = new Set(prev);
            next.has(code) ? next.delete(code) : next.add(code);
            return next;
        });
    };

    const canManage = viewAll || isProjectManager;

    useEffect(() => {
        fetchTasks();
        fetchMilestones();
    }, []);

    useEffect(() => {
        if (defaultCollapseApplied || tasks.length === 0) return;
        const seen = new Set();
        const codes = [];
        tasks.forEach((t) => {
            const key = t.milestone_code ? String(t.milestone_code) : "unassigned";
            if (!seen.has(key)) {
                seen.add(key);
                codes.push(key);
            }
        });
        const toCollapse = codes.slice(2); // keep first 2 expanded, collapse rest
        setCollapsedMilestones(new Set(toCollapse));
        setDefaultCollapseApplied(true);
    }, [tasks, defaultCollapseApplied]);

    const fetchMilestones = async () => {
        try {
            const request = project.id
                ? await MilestoneService.getAllByProject(project.id)
                : await MilestoneService.getAllMilestone();
            if (request?.status) setMilestones(request.data || []);
        } catch (error) {
            console.error(error);
        }
    };

    const fetchTasks = async () => {
        try {
            setLoading(true);
            const request = project.id
                ? await ProjectTaskService.getAllByProject(project.id)
                : await ProjectTaskService.getAllTask();

            if (request?.status) {
                let rawRows = request.data || [];

   if (!viewAll) {
                    const user = await UserService.getOmeUser();
                    const uid = String(user.uid).trim();
                    const uname = (user.name || "").trim().toLowerCase();

                    const managerMatch = String(project.managerId ?? "").trim() === uid;
                    setIsProjectManager(managerMatch);

                    if (!managerMatch) {
                        rawRows = rawRows.filter((t) => {
                            const addedByMatch = String(t.added_by ?? "").trim() === uid;
                            const assigneeMatch = (t.assignee || "").trim().toLowerCase() === uname;
                            return addedByMatch || assigneeMatch;
                        });
                    }
                } else {
                    setIsProjectManager(true);
                }
                setPermissionsReady(true);

                const rows = rawRows.map((t) => ({
                    dbId: t.id,
                    code: t.task_code,
                    name: t.task_name,
                    milestone_code: t.milestone_code,
                    task_type: t.task_type,
                    assignee: t.assignee,
                    priority: t.priority,
                    status: t.status,
                    start_date: t.start_date ? dayjs(t.start_date).format("DD-MMM-YYYY") : "-",
                    due_date: t.due_date ? dayjs(t.due_date).format("DD-MMM-YYYY") : "-",
                    progress: t.progress || 0,
                    time_spent: t.time_spent || 0,
                }));
                setTasks(rows);
            } else {
                setTasks([]);
            }
        } catch (error) {
            console.error("Error fetching tasks:", error);
            setTasks([]);
        } finally {
            setLoading(false);
        }
    };

    const milestoneNameMap = {};
    milestones.forEach((m) => { milestoneNameMap[String(m.milestone_code)] = m.milestone_name; });

    const filteredData = tasks.filter((t) => {
        const text = searchText.toLowerCase();
        const matchesSearch = !text || (t.name || "").toLowerCase().includes(text);
        const matchesStatus = !statusFilter || t.status === statusFilter;
        const matchesPriority = !priorityFilter || t.priority === priorityFilter;
        const matchesMilestone = !milestoneFilter || t.milestone_code === milestoneFilter;
        return matchesSearch && matchesStatus && matchesPriority && matchesMilestone;
    });

    const completedCount = tasks.filter((t) => t.status === "Completed").length;
    const inProgressCount = tasks.filter((t) => t.status === "In Progress").length;
    const todoCount = tasks.filter((t) => t.status === "To Do").length;
    const onHoldCount = tasks.filter((t) => t.status === "On Hold").length;
    const cancelledCount = tasks.filter((t) => t.status === "Cancelled").length;
    const STATUS_PROGRESS_WEIGHT = {
        "Completed": 100,
        "In Progress": 50,
        "To Do": 0,
        "On Hold": 0,
        "Cancelled": 0,
    };

    const getEffectiveProgress = (t) => {
        // Prefer the task's own progress value if it's meaningfully set,
        // otherwise fall back to a weight based on status.
        if (typeof t.progress === "number" && t.progress > 0) return t.progress;
        return STATUS_PROGRESS_WEIGHT[t.status] ?? 0;
    };

    const overallProgress = tasks.length
        ? Math.round(tasks.reduce((s, t) => s + getEffectiveProgress(t), 0) / tasks.length)
        : 0;

    const groupedByMilestone = [];
    const seenMilestones = new Set();
    filteredData.forEach((t) => {
        const key = t.milestone_code ? String(t.milestone_code) : "unassigned";
        if (!seenMilestones.has(key)) {
            seenMilestones.add(key);
            groupedByMilestone.push({
                milestone_code: key,
                milestone_name: key === "unassigned" ? "No Milestone" : (milestoneNameMap[key] || key),
                tasks: [],
            });
        }
        groupedByMilestone.find((g) => g.milestone_code === key).tasks.push({
            ...t,
            milestone_name: key === "unassigned" ? "No Milestone" : (milestoneNameMap[key] || key),
        });
    });

    const goAddTask = () =>
        navigate("/overview-project/tasks/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                pro_engineer: project.engineerIds,
                pro_department_name: project.departmentName,
            },
        });

const goViewEditTask = (record, editable) =>
        navigate("/overview-project/tasks/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                taskDbId: record.dbId,
                action: editable ? "Edit" : "View",
                enableEdit: editable,
                statusOnly: !canManage,
                canManage,
                pro_engineer: project.engineerIds,
                pro_department_name: project.departmentName,
            },
        });

    const columns = (offset = 0) => [
        { title: "#", key: "sno", width: 45, align: "center", render: (_, __, i) => offset + i + 1 },
        { title: "Task Name", dataIndex: "name", key: "name", width: 180, render: (t) => <Text strong>{t}</Text> },
        { title: "Task Type", dataIndex: "task_type", key: "task_type", width: 120, render: (t) => t || "-" },
        { title: "Assignee", dataIndex: "assignee", key: "assignee", width: 140, render: (t) => t || "-" },
        {
            title: "Priority", dataIndex: "priority", key: "priority", width: 100,
            render: (v) => v ? (
                <span style={{
                    background: `${PRIORITY_COLORS[v] || "#bfbfbf"}1a`, color: PRIORITY_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v}</span>
            ) : "-",
        },
        {
            title: "Status", dataIndex: "status", key: "status", width: 120,
            render: (v) => (
                <span style={{
                    background: `${STATUS_COLORS[v] || "#bfbfbf"}1a`, color: STATUS_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v || "-"}</span>
            ),
        },
        { title: "Start Date", dataIndex: "start_date", key: "start_date", width: 110 },
        { title: "Due Date", dataIndex: "due_date", key: "due_date", width: 110 },
        {
            title: "Progress", dataIndex: "progress", key: "progress", width: 130,
            render: (v) => <Progress percent={v || 0} size="small" />,
        },
        { title: "Time Spent", dataIndex: "time_spent", key: "time_spent", width: 100, render: (v) => `${v || 0}h` },
        {
            title: "Action", key: "action", align: "center", width: 90,
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" icon={<EyeOutlined />} disabled={!permissionsReady} onClick={(e) => { e.stopPropagation(); goViewEditTask(record, false); }} />
                    <Button size="small" icon={<EditOutlined />} disabled={!permissionsReady} onClick={(e) => { e.stopPropagation(); goViewEditTask(record, true); }} />
                </Space>
            ),
        },
    ];

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                    <Space align="center" size={8}>
                        <ArrowLeftOutlined
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}
                        />
                        <Typography.Title level={4} style={{ margin: 0, color: "#1658b4" }}>Tasks</Typography.Title>
                        <Tag color="green">{project.status}</Tag>
                    </Space>
                </div>
                <Space wrap>
                    <Button size="middle" onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}>
                        View Project
                    </Button>
                    {canManage && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={goAddTask}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                            Add Task
                        </Button>
                    )}
                    <Button size="middle" icon={<DownOutlined />}>More Actions</Button> 
                </Space>
            </div>

            <div style={{ padding: "12px 16px 0" }}>
                <Row gutter={16}>
                    <Col xs={24} lg={16}>
                        <Card bodyStyle={{ padding: "14px 20px" }} style={{ height: "100%" }}>
                            <Space align="start" size={12}>
                                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f9f0ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📁</div>
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 15 }}>{project.pro_name}</Text>
                                        <Tag color="cyan" style={{ fontSize: 10, marginLeft: 10 }}>{project.pro_no}</Tag>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Customer</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.customer}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Manager</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{managerName}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Start Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.start_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>End Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.end_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Value</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>₹{Number(project.value).toLocaleString("en-IN")}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Status</div>
                                            <Tag color="blue" style={{ fontSize: 11 }}>{project.status}</Tag>
                                        </div>
                                    </div>
                                </div>
                            </Space>
                        </Card>
                    </Col>
                    <Col xs={24} lg={8}>
                        <Card bodyStyle={{ padding: "10px 16px" }} style={{ height: "100%" }}>
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "flex-start", flexWrap: "wrap", gap: 32 }}>
                                <div style={{ minWidth: 90 }}>
                                    <div style={{ fontSize: 11, color: "#888" }}>Overall Progress</div>
                                    <div style={{ fontSize: 24, fontWeight: 700, color: "#1677ff", lineHeight: 1.2 }}>{overallProgress}%</div>
                                    <Progress percent={overallProgress} size="small" showInfo={false} style={{ width: 90 }} />
                                </div>

                                <div style={{ display: "flex", flexDirection: "column", gap: 4, fontSize: 12, marginLeft: 48 }}>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#52c41a", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ width: 78, display: "inline-block" }}>Completed</span>
                                        <b style={{ width: 20, display: "inline-block", textAlign: "right" }}>{completedCount}</b>
                                        <span style={{ marginLeft: 4 }}>({tasks.length ? Math.round((completedCount / tasks.length) * 100) : 0}%)</span>
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#fa8c16", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ width: 78, display: "inline-block" }}>In Progress</span>
                                        <b style={{ width: 20, display: "inline-block", textAlign: "right" }}>{inProgressCount}</b>
                                        <span style={{ marginLeft: 4 }}>({tasks.length ? Math.round((inProgressCount / tasks.length) * 100) : 0}%)</span>
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#1677ff", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ width: 78, display: "inline-block" }}>To Do</span>
                                        <b style={{ width: 20, display: "inline-block", textAlign: "right" }}>{todoCount}</b>
                                        <span style={{ marginLeft: 4 }}>({tasks.length ? Math.round((todoCount / tasks.length) * 100) : 0}%)</span>
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#8c8c8c", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ width: 78, display: "inline-block" }}>On Hold</span>
                                        <b style={{ width: 20, display: "inline-block", textAlign: "right" }}>{onHoldCount}</b>
                                        <span style={{ marginLeft: 4 }}>({tasks.length ? Math.round((onHoldCount / tasks.length) * 100) : 0}%)</span>
                                    </span>
                                    <span style={{ display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }}>
                                        <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#f5222d", display: "inline-block", flexShrink: 0 }} />
                                        <span style={{ width: 78, display: "inline-block" }}>Cancelled</span>
                                        <b style={{ width: 20, display: "inline-block", textAlign: "right" }}>{cancelledCount}</b>
                                        <span style={{ marginLeft: 4 }}>({tasks.length ? Math.round((cancelledCount / tasks.length) * 100) : 0}%)</span>
                                    </span>
                                </div>
                            </div>
                            <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 6 }}>Total Tasks: {tasks.length}</Text>
                        </Card>
                    </Col>
                </Row>
            </div>

            <div style={{ padding: "12px 16px 16px" }}>
                <Card
                    title={<Text strong style={{ fontSize: 16 }}>Task List</Text>}
                    bodyStyle={{ padding: 12 }}
                >
                    <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                        <Row gutter={[16, 16]}>
                            <Col xs={24} sm={12} md={6}>
                                <Input
                                    allowClear
                                    placeholder="Search task..."
                                    prefix={<SearchOutlined />}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </Col>
                            <Col xs={24} sm={12} md={5}>
                                <Select allowClear placeholder="All Status" style={{ width: "100%" }}
                                    value={statusFilter} onChange={setStatusFilter}
                                    options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
                            </Col>
                            <Col xs={24} sm={12} md={5}>
                                <Select allowClear placeholder="All Priority" style={{ width: "100%" }}
                                    value={priorityFilter} onChange={setPriorityFilter}
                                    options={Object.keys(PRIORITY_COLORS).map((s) => ({ value: s, label: s }))} />
                            </Col>
                            <Col xs={24} sm={12} md={5}>
                                <Select allowClear placeholder="All Milestone" style={{ width: "100%" }}
                                    value={milestoneFilter} onChange={setMilestoneFilter}
                                    options={milestones.map((m) => ({ value: m.milestone_code, label: m.milestone_name }))} />
                            </Col>
                            <Col xs={24} sm={12} md={3}>
                                <Space>
                                    <Button icon={<FilterOutlined />}>More</Button>
                                    <Button icon={<DownloadOutlined />} />
                                </Space>
                            </Col>
                        </Row>
                    </Card>

                    {tasks.length === 0 && !loading ? (
                        <Empty description="No tasks yet" />
                    ) : (
                        <>
                            <style>{`
                                .header-only-table .ant-table-tbody,
                                .header-only-table .ant-table-placeholder {
                                    display: none !important;
                                }
                                .header-only-table .ant-table-container {
                                    border-bottom: none !important;
                                }
                            `}</style>
                            <Table
                                className="header-only-table"
                                rowKey="dbId"
                                dataSource={[]}
                                columns={columns(0)}
                                pagination={false}
                                size="small"
                                scroll={{ x: "max-content" }}
                                showHeader={true}
                                tableLayout="fixed"
                            />
                            {groupedByMilestone.reduce((acc, group) => {
                                const offset = acc.runningCount;
                                acc.runningCount += group.tasks.length;
                                acc.elements.push({ group, offset });
                                return acc;
                            }, { runningCount: 0, elements: [] }).elements.map(({ group, offset }, idx) => {
                                const isCollapsed = collapsedMilestones.has(group.milestone_code);
                                return (
                                    <div key={group.milestone_code} style={{ marginBottom: 0 }}>
                                        <div
                                            onClick={() => toggleMilestone(group.milestone_code)}
                                            style={{
                                                display: "flex", alignItems: "center", gap: 8,
                                                fontSize: 13, fontWeight: 600, color: "#000", cursor: "pointer",
                                                padding: "8px 8px", borderTop: "1px solid #f0f0f0",
                                                borderBottom: "1px solid #f0f0f0", background: "#fafafa",
                                            }}
                                        >
                                            <DownOutlined
                                                style={{
                                                    fontSize: 11,
                                                    transition: "transform 0.2s",
                                                    transform: isCollapsed ? "rotate(-90deg)" : "rotate(0deg)",
                                                }}
                                            />
                                            {group.milestone_code === "unassigned"
                                                ? group.milestone_name
                                                : `Milestone: ${idx + 1}. ${group.milestone_name}`}
                                            <Tag>{group.tasks.length} Tasks</Tag>
                                        </div>
                                        {!isCollapsed && (
                                            <Table
                                                rowKey="dbId"
                                                dataSource={group.tasks}
                                                columns={columns(offset)}
                                                loading={loading}
                                                pagination={false}
                                                size="small"
                                                scroll={{ x: "max-content" }}
                                                showHeader={false}
                                                tableLayout="fixed"
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </Card>
            </div>
        </Card>
    );
};

export default TasksList;